<template>
	<div class="display-flex flex-column">
		<div>
			<ConditionalRenderingFieldDataProvider
				v-for="param in actionParams"
				:key="buildFieldKey(param, localParamsMap)"
				:param="param"
				:credentialsConfiguration="actionContextNode.credentials_configuration"
				:service="service"
				:paramsMap="localParamsMap"
				:paramsMapLabels="localParamsMapLabels"
				:nodeId="actionContextNode.id"
				:workflowVersion="editedWorkflowVersion"
				:hasEditPermissions="hasEditPermissions"
				@update-input-field="updateInputField"
				@update-input-field-label="updateInputFieldLabel"
			/>
		</div>
		<div class="text-align-right pt-1" v-if="allowChangeSampleFunctionality">
			<h4 class="text-underline" role="button" @click="$emit('change-sample-by-choosing-item')">
				Change sample by choosing {{ itemName }}
			</h4>
		</div>
	</div>
</template>

<script>
import ElementService from '@/utils/element-service';
import { mapActions, mapGetters } from 'vuex';
import ParamsService from '@/utils/ParamsService';
import debounce from 'lodash/debounce';
// mixin to get acceptable valueTypes for DataInputMenu
import ItemOnlyAllowedValueTypesMixin from '@/components/BlockEditorWidget/FieldSettings/ValueTypesMixins/ItemOnlyAllowedValueTypesMixin';
import ConditionalRenderingFieldDataProvider from '@/components/CreationWizard/Steps/ChoosePlacementForAddOn/ConditionalRenderingFieldDataProvider.vue';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { indefinite } from '@/utils/language-service';
import { getContext } from '@/store/modules/contexts/getters';
import { getPlacement } from '@/store/modules/placements/getters';
import { getService } from '@/store/modules/apps/getters';
export default {
	name: 'ActionContextNodeParams',
	props: { hasEditPermissions: { type: Boolean }, allowChangeSampleFunctionality: { type: Boolean } },
	mixins: [ItemOnlyAllowedValueTypesMixin],
	computed: {
		...mapGetters('selectedWorkflow', ['actionContextNode', 'element', 'isNativeElement']),
		...mapGetters(['actions/getActionParams']),
		...mapGettersShorthand({ editedWorkflowVersion, getContext, getPlacement, getService }),
		actionParams() {
			if (!this.actionContextNode) {
				return [];
			}
			return this['actions/getActionParams'](this.actionContextNode.action).filter((param) => !!param.source); // return only params with source
		},
		placement() {
			return this.getPlacement(this.element.placement);
		},
		context() {
			return this.getContext(this.element.context);
		},
		service() {
			return this.getService(this.placement.service);
		},
		itemName() {
			return indefinite(this.placement.item_name);
		},
	},
	components: { ConditionalRenderingFieldDataProvider },
	methods: {
		...mapActions(['elements/updateTestParamsMap']),
		buildFieldKey(param, paramsMap) {
			return ParamsService.buildFieldKey(param, paramsMap);
		},
		buildLocalParamsMap() {
			this.localParamsMap = ElementService.getElementFullParamsMap(this.element, this.context, this.isNativeElement);
			this.localParamsMapLabels = ElementService.getElementParamsMapLabels(this.element);
		},
		updateInputField(payload) {
			const { map, label, param } = payload;
			this.updateBlockParam(map);
			if (label) {
				this.updateInputFieldLabel({ label, param });
			}
		},
		updateInputFieldLabel(payload) {
			const { label } = payload;
			this.updateBlockParamLabels(label);
		},
		updateBlockParam(payload) {
			const { key, value } = payload;
			this.handleBlockParamDependencies(key);
			this.$set(this.localParamsMap, key, value);
			this.updateTestParamsMap();
		},
		updateTestParamsMap: debounce(function () {
			this['elements/updateTestParamsMap']({
				elementId: this.element.id,
				paramsMap: this.localParamsMap,
				paramsMapLabels: this.localParamsMapLabels,
				isNative: this.isNativeElement,
			});
		}, 300),
		handleBlockParamDependencies(keyChanged) {
			this.actionParams.forEach((param) => {
				const dependsOn = param.depends_on || [];
				if (dependsOn.includes(keyChanged)) {
					this.updateBlockParam({ key: param.key, value: '' });
					this.updateBlockParamLabels({ key: param.key, value: '' });
				}
			});
		},
		updateBlockParamLabels(payload) {
			const { key, value } = payload;
			this.$set(this.localParamsMapLabels, key, value);
			this.updateTestParamsMap();
		},
	},
	data: () => ({
		localParamsMap: {},
		localParamsMapLabels: {},
	}),
	mounted() {
		this.buildLocalParamsMap();
	},
};
</script>
<style lang="scss" scoped>
.desc {
	top: 0px !important;
}
</style>
